/**
 * UTS instance shape helpers (My Apps, Phase 3).
 *
 * The live `GET /uts/instances` payload is deeply nested and does NOT match
 * the design-spec's assumed flat field names. This module is the single place
 * that maps the real backend shape to the clean view-model the table/detail
 * render, so the mapping is documented, testable, and easy to adjust if the
 * backend changes.
 *
 * Verified real top-level keys (org-1, 2026-05):
 *   _id, name, app_type, origin, template_id, template_version,
 *   deploy_state, deployment_standard, owner:{user_id}, target:{…},
 *   audit:{created_at,updated_at,created_by,updated_by},
 *   reconciled_state, reconciled_branches:{<branch>:{components:{…},namespace,…}},
 *   current_snapshot_id, preflight_verdict, values, addons, …
 *
 * `deploy_state` observed values: draft, deploying, healthy, degraded,
 * failed, rolling_back. (The spec's "deployed" maps to "healthy".)
 */

/** The canonical id field (Mongo `_id`, e.g. `ti_…`). */
export function instanceId(inst) {
  return inst?._id || inst?.id || null
}

/** Created/updated timestamps live under `audit`. */
export function createdAt(inst) {
  return inst?.audit?.created_at || inst?.created_at || null
}
export function updatedAt(inst) {
  return inst?.audit?.updated_at || inst?.updated_at || null
}

/** Owner is `{ user_id }`; fall back to a created_by string. */
export function ownerId(inst) {
  return inst?.owner?.user_id ?? inst?.audit?.created_by ?? null
}

/**
 * Organization the instance belongs to. Useful since the My Apps list
 * can now span multiple orgs (the user's active org + any org they have
 * a cross-org `workload:viewer` grant on). The UI surfaces this as a
 * small chip next to the row name.
 */
export function organizationId(inst) {
  return inst?.owner?.organization_id ?? null
}

/** Cluster / namespace from `target` (top-level, branch-independent). */
export function clusterName(inst) {
  return inst?.target?.cluster_name || '—'
}
export function namespaceName(inst) {
  return inst?.target?.namespace || '—'
}

/**
 * Health classification for the status pill — a light (green/yellow/red/grey)
 * + label.
 *
 * REALITY-FIRST: we derive the pill from `reconciled_state` (what the
 * reconciler observed in the cluster ~every tick) when it's present, and fall
 * back to `deploy_state` (the deploy-time INTENT) only when it isn't. This is
 * the same precedence the cluster-api dashboard's detail tab uses, and it
 * fixes the long-standing "stuck Deploying" bug: a cs-application's
 * `deploy_state` is never flipped back to healthy after its async GitLab
 * pipeline finishes, while `reconciled_state` correctly reads `healthy` from
 * the live 2/2 pods. `deploy_state` is still exposed via `deployPhase()` for a
 * secondary "deploy phase" chip.
 */
const STATE_META = {
  healthy: { light: 'green', label: 'Healthy', pill: 'deployed' },
  deployed: { light: 'green', label: 'Healthy', pill: 'deployed' },
  degraded: { light: 'yellow', label: 'Degraded', pill: 'degraded' },
  deploying: { light: 'yellow', label: 'Deploying', pill: 'deploying' },
  rolling_back: { light: 'yellow', label: 'Rolling back', pill: 'rolling_back' },
  draft: { light: 'grey', label: 'Draft', pill: 'draft' },
  failed: { light: 'red', label: 'Failed', pill: 'failed' },
  suspended: { light: 'grey', label: 'Suspended', pill: 'suspended' }
}

// reconciled_state carries richer values than deploy_state — normalize the
// reconciler-specific variants onto the same pill vocabulary.
const RECON_STATE_META = {
  healthy: { light: 'green', label: 'Healthy', pill: 'deployed' },
  healthy_no_pipeline: { light: 'green', label: 'Healthy', pill: 'deployed' },
  healthy_with_failed_pipeline: { light: 'yellow', label: 'Healthy (pipeline failed)', pill: 'degraded' },
  deploying: { light: 'yellow', label: 'Deploying', pill: 'deploying' },
  degraded: { light: 'yellow', label: 'Degraded', pill: 'degraded' },
  warning: { light: 'yellow', label: 'Warning', pill: 'degraded' },
  drifted: { light: 'yellow', label: 'Drifted', pill: 'degraded' },
  failed: { light: 'red', label: 'Failed', pill: 'failed' },
  unknown: { light: 'grey', label: 'Unknown', pill: 'unknown' }
}

/**
 * The reconciled state of the branch the customer is VIEWING (their own
 * environment), NOT the cross-branch aggregate. The platform reconciler rolls
 * ALL branches (development/stage/main) into one instance `reconciled_state`
 * with an "any branch failed → failed" rule, so a perfectly healthy
 * `development` deploy shows `failed` when a sibling `stage` branch's pipeline
 * failed. A customer only cares about the environment they're in, so we scope
 * the badge to the branch whose namespace matches the deployed namespace.
 * Returns null (→ fall back to the aggregate) when per-branch state is absent.
 */
export function activeBranchState(inst) {
  const branches = inst?.reconciled_branches
  if (!branches || typeof branches !== 'object') return null
  const names = Object.keys(branches)
  if (!names.length) return null
  const ns = String((inst?.target && inst.target.namespace) || inst?.namespace || '').toLowerCase()
  const env = String(inst?.environment || '').toLowerCase()
  let chosen = null
  if (ns || env) {
    const name = names.find((n) => {
      const b = branches[n] || {}
      const bns = String(b.namespace || '').toLowerCase()
      return bns === ns || n.toLowerCase() === ns || bns === env || n.toLowerCase() === env
    })
    if (name) chosen = branches[name]
  }
  if (!chosen && names.length === 1) chosen = branches[names[0]]
  if (!chosen || typeof chosen !== 'object') return null
  const st = chosen.state || chosen.reconciled_state
  return st ? String(st).toLowerCase() : null
}

/**
 * Name of the branch `activeBranchState` reads from, or null. Split out so
 * callers that need the branch's other fields (its tick states, its pipeline
 * timestamps) resolve exactly the same branch the status badge used.
 */
export function activeBranchName(inst) {
  const branches = inst?.reconciled_branches
  if (!branches || typeof branches !== 'object') return null
  const names = Object.keys(branches)
  if (!names.length) return null
  const ns = String((inst?.target && inst.target.namespace) || inst?.namespace || '').toLowerCase()
  const env = String(inst?.environment || '').toLowerCase()
  if (ns || env) {
    const name = names.find((n) => {
      const b = branches[n] || {}
      const bns = String(b.namespace || '').toLowerCase()
      return bns === ns || n.toLowerCase() === ns || bns === env || n.toLowerCase() === env
    })
    if (name) return name
  }
  return names.length === 1 ? names[0] : null
}

/**
 * The timestamp that actually belongs to the state `stateMeta()` renders, and
 * — just as important — how much that timestamp is allowed to claim.
 *
 * `audit.updated_at` is the last time the Mongo DOCUMENT was written (a config
 * edit, a reconciler promotion, an owner backfill). Pairing it with a live
 * status reads as "this state started N ago", which is simply false: on
 * 2026-09-13 cluster-api/development carried updated_at=2026-08-28 while its
 * state flipped to `deploying` at 08:45Z that morning.
 *
 * Returns `{ at, kind }`:
 *   kind 'changed' — `at` is when this state BEGAN. Only returned when the
 *                    backend recorded the transition, or when the reconciler
 *                    tick window actually contains the change (a run of ticks
 *                    in this state preceded by a tick in a different one).
 *   kind 'checked' — `at` is only when the state was last OBSERVED. We do not
 *                    know when it began — every tick we can see says the same
 *                    thing, so the change is older than the window.
 *   kind 'updated' — nothing better exists; `at` is the document write time.
 *
 * Callers MUST word 'checked' / 'updated' differently from 'changed'. An
 * honest "checked 2m ago" beats a precise-looking lie about when it started.
 */
export function stateEventAt(inst) {
  const state = activeBranchState(inst) || String(inst?.reconciled_state || '').toLowerCase() || null

  // 1. The reconciler tick window. Branch-scoped and the most precise signal we
  //    have, so it outranks the doc-level `degraded_since`.
  const ticks = inst?.reconciliation?.recent_ticks
  if (state && Array.isArray(ticks) && ticks.length) {
    const branch = activeBranchName(inst)
    const ordered = ticks
      .filter((t) => t && !t.skipped && t.started_at && !Number.isNaN(Date.parse(t.started_at)))
      .sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at))
    const stateOf = (t) => {
      if (branch) {
        const b = (t.branches || []).find((x) => x && x.branch === branch)
        if (b && b.state) return String(b.state).toLowerCase()
      }
      return t.new_state ? String(t.new_state).toLowerCase() : null
    }
    // Walk back over the trailing run of ticks that agree with the state now.
    let runStart = -1
    for (let i = ordered.length - 1; i >= 0; i -= 1) {
      if (stateOf(ordered[i]) !== state) break
      runStart = i
    }
    // runStart > 0 means we can SEE the tick before the run, and it disagreed —
    // that is an observed transition. runStart === 0 means the whole window is
    // this state, so the change predates it and we must not date it.
    if (runStart > 0) return { at: ordered[runStart].started_at, kind: 'changed' }
  }

  // 2. Backend-recorded degradation time. Doc-level, so only trust it when the
  //    aggregate state agrees with the branch we are describing.
  if (
    state === 'degraded' &&
    inst?.degraded_since &&
    String(inst?.reconciled_state || '').toLowerCase() === 'degraded'
  ) {
    return { at: inst.degraded_since, kind: 'changed' }
  }

  // 3. We know when it was last looked at, nothing more.
  const checked = inst?.reconciled_at || inst?.reconciliation?.last_polled_at
  if (checked) return { at: checked, kind: 'checked' }

  // 4. Nothing but the document write.
  return { at: updatedAt(inst), kind: 'updated' }
}

/**
 * The reality-first status pill. Prefers the ACTIVE branch's reconciled state
 * (so a failed sibling branch never marks a healthy env failed), then the
 * aggregate `reconciled_state`, then `deploy_state`. Use this everywhere the
 * headline "Status" is shown.
 */
export function stateMeta(inst) {
  const bs = activeBranchState(inst)
  if (bs && RECON_STATE_META[bs]) return RECON_STATE_META[bs]
  const rs = String(inst?.reconciled_state || '').toLowerCase()
  if (bs && !RECON_STATE_META[bs] && !rs) {
    return { light: 'grey', label: prettyState(bs) || 'Unknown', pill: 'unknown' }
  }
  if (rs && RECON_STATE_META[rs]) return RECON_STATE_META[rs]
  if (rs) return { light: 'grey', label: prettyState(rs) || 'Unknown', pill: 'unknown' }
  const s = String(inst?.deploy_state || '').toLowerCase()
  return STATE_META[s] || { light: 'grey', label: prettyState(s) || 'Unknown', pill: s || 'unknown' }
}

/**
 * The deploy-time INTENT, for a secondary "deploy phase" chip. Returns null
 * when it adds no information (matches the reality pill, or is absent), so the
 * UI only shows it when it diverges (e.g. reality Healthy + phase Deploying).
 */
export function deployPhase(inst) {
  const ds = String(inst?.deploy_state || '').toLowerCase()
  if (!ds) return null
  // Compare against the same branch-scoped reality the badge uses.
  const rs = activeBranchState(inst) || String(inst?.reconciled_state || '').toLowerCase()
  // Hide when redundant: no reconciled signal, or both map to the same pill.
  if (!rs) return null
  const dsMeta = STATE_META[ds]
  const rsMeta = RECON_STATE_META[rs]
  if (dsMeta && rsMeta && dsMeta.pill === rsMeta.pill) return null
  return { key: ds, label: prettyState(ds) }
}

export function prettyState(s) {
  if (!s) return ''
  return String(s)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Aggregate pod ready/desired across every reconciled branch + component.
 * Returns `{ ready, desired }`. Falls back to `{0,0}` when no reconciliation
 * data is present (e.g. a fresh draft).
 */
export function podCounts(inst) {
  let ready = 0
  let desired = 0
  const branches = inst?.reconciled_branches || {}
  Object.values(branches).forEach((branch) => {
    const comps = branch?.components || {}
    Object.values(comps).forEach((c) => {
      // Prefer explicit replica fields; fall back to pod_summary counts.
      const d = c?.replicas_desired
      const r = c?.replicas_ready
      if (typeof d === 'number') desired += d
      else if (c?.pod_summary?.pod_count != null) desired += c.pod_summary.pod_count
      if (typeof r === 'number') ready += r
      else if (c?.pod_summary?.running != null) ready += c.pod_summary.running
    })
  })
  return { ready, desired }
}

/** Human "2/3" pod string; empty-dash when nothing scheduled. */
export function podLabel(inst) {
  const { ready, desired } = podCounts(inst)
  if (!desired && !ready) return '—'
  return `${ready}/${desired}`
}

/** App-type chip text (e.g. "stack", "application"). */
export function appType(inst) {
  return inst?.app_type || '—'
}

/**
 * Is this instance a CS/denv-new **cs-application** (one Deployment per
 * branch, namespace == branch, config-repo `.denv`/`.env`, optional GitLab
 * project)? Anything else is a **catalog** stack (kafka-cluster,
 * monitoring-stack, …) whose resources are torn down by the UTS cascade.
 *
 * Mirrors the backend rule in cluster-api
 * `services/uts/instance_shape.py::is_cs_application` — keep the two in
 * sync. Any ONE of these marks a cs-application:
 *   - `template_id === 'cs-application'` (marketplace-deployed cs-app; its
 *     `origin` is still `catalog`),
 *   - `deployment_standard === 'denv-standard'`,
 *   - `origin === 'gitlab-import'`.
 */
export function isCsApplication(inst) {
  if (!inst || typeof inst !== 'object') return false
  const lower = (v) => String(v || '').trim().toLowerCase()
  if (lower(inst.template_id) === 'cs-application') return true
  if (lower(inst.deployment_standard) === 'denv-standard') return true
  if (lower(inst.origin) === 'gitlab-import') return true
  return false
}

/**
 * The delete-dialog kind for an instance: `'cs-application'` (rich
 * branch/config-repo/GitLab delete) or `'catalog'` (cascade delete of the
 * rendered manifests + optional data volumes). The backend delete-preview
 * echoes the same value in `preview.kind`; that is authoritative when
 * present — this is the client-side guess used before the preview lands.
 */
export function deleteKind(inst) {
  return isCsApplication(inst) ? 'cs-application' : 'catalog'
}

/** Template display: id + version (the catalog template name isn't on the row). */
export function templateLabel(inst) {
  const id = inst?.template_id || '—'
  const v = inst?.template_version
  return v ? `${id} ${v}` : id
}

/**
 * The string the typed-confirmation delete must match. The delete-preview
 * returns an authoritative `workload_name`; until we have it, the instance
 * `name` is the best client-side guess for the confirmation prompt label.
 */
export function workloadName(inst) {
  return inst?.name || ''
}

/** Relative "time ago" formatter (no external dep). */
export function timeAgo(iso) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000))
  const units = [
    ['y', 31536000],
    ['mo', 2592000],
    ['d', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1]
  ]
  for (const [label, size] of units) {
    if (secs >= size) {
      const n = Math.floor(secs / size)
      return `${n}${label} ago`
    }
  }
  return 'just now'
}

/** Absolute timestamp for the `title=` tooltip. */
export function absoluteTime(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

/**
 * Project a raw instance into the flat view-model the DataTable binds to.
 * Keeping the raw object on `_raw` lets the detail view reach nested fields.
 */
export function toRow(inst) {
  const meta = stateMeta(inst)
  return {
    id: instanceId(inst),
    name: inst?.name || '(unnamed)',
    origin: inst?.origin || '—',
    appType: appType(inst),
    templateId: inst?.template_id || '—',
    templateLabel: templateLabel(inst),
    cluster: clusterName(inst),
    namespace: namespaceName(inst),
    // Target cluster's environment (development / stage / production), stamped
    // by the backend from cluster-metadata.json. null when unresolved → no badge.
    environment: inst?.environment || null,
    deployState: inst?.deploy_state || 'unknown',
    health: meta.light,
    healthLabel: meta.label,
    pill: meta.pill,
    pods: podLabel(inst),
    ownerId: ownerId(inst),
    organizationId: organizationId(inst),
    updatedAt: updatedAt(inst),
    updatedAgo: timeAgo(updatedAt(inst)),
    updatedAbs: absoluteTime(updatedAt(inst)),
    // Per-row edit permission from the list endpoint (new tri-state access
    // tree: Edit = workload:editor). When the backend doesn't supply a
    // `permissions` block (older build), default OPEN so we don't hide Edit
    // for everyone — the backend still enforces on the actual call.
    canEdit: inst?.permissions ? !!inst.permissions.can_edit : true,
    _raw: inst
  }
}
